// UserManagement.tsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
} from "@mui/material";
import {
  Delete,
  ExpandMore,
  ExpandLess,
  Search,
  Add,
  Close,
  ChangeCircle,
} from "@mui/icons-material";
import { axiosRequest } from "../../service/AxiosService";
import theme from "../../style/theme";
import CustomMenu from "../../components/CustomMenu/CustomMenu";
import {
  Groups,
  RecordUserDataInitResponse,
  RecordUserDataModel,
  Roles,
  UserGroup,
  UserProfiles,
} from "../../type/response/RecordUserDataInitResponse";
import CreateUserDialog from "./components/CreateUserDialog";
import UserManageTable from "./components/UserManageTable";
import dayjs from "dayjs";

export const formDefaultValue: RecordUserDataModel = {
  userId: null,
  firstName: "",
  middleName: "",
  lastName: "",
  nickname: "",
  email: "",
  password: "",
  userRoles: null,
  userProfiles: null,
  userGroup: null,
  isDeleted: false,
};

/**
 * テーブルの列定義
 */
export const columnLabels: Partial<Record<keyof RecordUserDataModel, string>> =
{
  lastName: "名字",
  middleName: "ミドルネーム",
  firstName: "名前",
  nickname: "ニックネーム",
  email: "メールアドレス",
  userRoles: "ユーザーロール",
  userProfiles: "ユーザー情報",
  userGroup: "ユーザー所属情報",
};

/**
 * 入力項目パターン
 */
export const inputPattern = {
  email: new RegExp("^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$"),
  password: new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[\\W_]).{8,}$"),
};

/**
 * ファイル取込処理
 */
export const handleFileImport = (
  e: React.ChangeEvent<HTMLInputElement>,
  rolesOptions: Roles[],
  groupsOptions: Groups[],
  handleUpdateRows: React.Dispatch<React.SetStateAction<RecordUserDataModel[]>>,
  handleSaveFileData?: React.Dispatch<
    React.SetStateAction<RecordUserDataModel[]>
  >
) => {
  // ファイル存在チェック
  if (e.target.files && e.target.files.length > 0) {
    const file = e.target.files[0];
    // ファイルサイズ・ファイル形式チェック
    if (file.size > 0 && file.type === "text/csv") {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target && e.target.result) {
          const fileText = e.target.result.toString(); // ファイルの中身を文字列として取得
          const fileRowValues = fileText.split(
            ["\r\n", "\r", "\n"].filter((nl) => fileText.includes(nl))[0]
          ); // 改行コードで分割
          // ファイルの中身を型リストに変換
          const fileRows = fileRowValues.map((val) => {
            const [
              userId,
              firstName,
              middleName,
              lastName,
              nickname,
              email,
              password,
              userRoles,
              userProfiles,
              userGroup,
            ] = val.split(",");
            return {
              userId: userId,
              firstName: firstName,
              middleName: middleName,
              lastName: lastName,
              nickname: nickname,
              email: email,
              password: password,
              userRoles: userRoles
                ? userRoles.split(",").map((roleName) =>
                  rolesOptions.find((v) => v.name === roleName.trim())
                ).filter(Boolean) as Roles[]
                : null,
              userProfiles: userProfiles.split("&").map((v) => {
                const [key, value] = v.split(":");
                return {
                  key: key,
                  value: value,
                  isDeleted: false,
                } as UserProfiles;
              }),
              userGroup: userGroup.split("&").map((v) => {
                const [groups, userGroupHistories] = v.split(":");
                const [date, notes] = userGroupHistories.split("_");
                const [startDate, endDate] = date.split("~");
                return {
                  groups: groupsOptions.filter((v) => v.name === groups)[0],
                  userGroupHistories: {
                    startDate: dayjs(startDate).toDate(),
                    endDate: dayjs(endDate).toDate(),
                    notes: notes,
                  },
                };
              }),
            } as RecordUserDataModel;
          });
          // ファイルデータを保持
          handleUpdateRows((prevRows) =>
            handleSaveFileData
              ? [...fileRows, ...prevRows].map((row, index) => ({
                ...row,
                userId: index.toString(),
              }))
              : prevRows.map((row) =>
                fileRows.map((v) => v.userId).includes(row.userId)
                  ? fileRows.filter((v) => v.userId === row.userId)[0]
                  : row
              )
          );
          if (handleSaveFileData) handleSaveFileData(fileRows);
        }
      };
      reader.readAsText(file, "UTF-8");
      e.target.value = ""; // ファイルをリセット
    }
  }
};

/**
 * ユーザー情報を文字列として取得
 */
export const getUserProfilesStr = (userProfiles: UserProfiles[] | null) => {
  // userProfilesが配列でない場合の安全チェック
  if (!userProfiles || !Array.isArray(userProfiles)) {
    return "";
  }
  return userProfiles.map((v) => `${v.key ?? ""}:${v.value ?? ""}`).join(",");
};

/**
 * ユーザー所属情報を文字列として取得
 */
export const getUserGroupStr = (userGroup: UserGroup[] | null) => {
  // userGroupが配列でない場合の安全チェック
  if (!userGroup || !Array.isArray(userGroup)) {
    return "";
  }
  return userGroup
    .map((v) => {
      const startDate = v.userGroupHistories?.startDate
        ? dayjs(v.userGroupHistories?.startDate).format("YYYY/MM/DD")
        : "";
      const endDate = v.userGroupHistories?.endDate
        ? dayjs(v.userGroupHistories?.endDate).format("YYYY/MM/DD")
        : "";
      const notes = v.userGroupHistories?.notes
        ? `(${v.userGroupHistories.notes})`
        : "";
      return `${v.groups?.name ?? ""}:${startDate}~${endDate} ${notes}`;
    })
    .join(",");
};

/**
 * ユーザーロールを文字列として取得
 */
export const getUserRolesStr = (userRoles: Roles[] | null) => {
  // userRolesが配列でない場合の安全チェック
  if (!userRoles || !Array.isArray(userRoles)) {
    return "";
  }
  return userRoles.map((v) => v.name).join(",");
};

/**
 * ユーザーを文字列として取得
 */
const getRecordUserDataModelStr = (row: RecordUserDataModel) => {
  return {
    lastName: row.lastName ?? "",
    middleName: row.middleName ?? "",
    firstName: row.firstName ?? "",
    nickname: row.nickname ?? "",
    email: row.email ?? "",
    userRoles: getUserRolesStr(row.userRoles),
    userProfiles: getUserProfilesStr(row.userProfiles),
    userGroup: getUserGroupStr(row.userGroup),
  } as Partial<Record<keyof RecordUserDataModel, string>>;
};

/**
 * 文字列が未入力の場合nullに変換
 */
const convertToStringOrNull = (str: string | null | undefined) => {
  return str && str !== "" ? str : null;
};

/**
 * ユーザーのフォーマット整形(サーバー通信)
 */
const formatUserDataList = (userDataList: RecordUserDataModel[]) => {
  return userDataList.map(
    (model) =>
      ({
        userId: convertToStringOrNull(model.userId),
        firstName: convertToStringOrNull(model.firstName),
        middleName: convertToStringOrNull(model.middleName),
        lastName: convertToStringOrNull(model.lastName),
        nickname: convertToStringOrNull(model.nickname),
        email: convertToStringOrNull(model.email),
        password: convertToStringOrNull(model.password),
        userRoles: model.userRoles ?? null,
        userProfiles: model.userProfiles
          ? model.userProfiles.map(
            (v) =>
              ({
                id: convertToStringOrNull(v.id),
                key: convertToStringOrNull(v.key),
                value: convertToStringOrNull(v.value),
                isDeleted: v.isDeleted ?? false,
              }) as UserProfiles
          )
          : null,
        userGroup: model.userGroup
          ? model.userGroup.map((v) => ({
            userGroupHistories: v.userGroupHistories
              ? {
                id: convertToStringOrNull(v.userGroupHistories.id),
                startDate: v.userGroupHistories.startDate
                  ? dayjs(
                    dayjs(v.userGroupHistories.startDate).format(
                      "YYYY-MM-DD"
                    )
                  ).toDate()
                  : null,
                endDate: v.userGroupHistories.endDate
                  ? dayjs(
                    dayjs(v.userGroupHistories.endDate).format(
                      "YYYY-MM-DD"
                    )
                  ).toDate()
                  : null,
                notes: convertToStringOrNull(v.userGroupHistories.notes),
              }
              : null,
            groups: v.groups
              ? ({
                id: v.groups.id,
                name: v.groups.name,
                isDeleted: v.groups.isDeleted,
              } as Groups)
              : null,
          }))
          : null,
        isDeleted: model.isDeleted ?? false,
      }) as RecordUserDataModel
  );
};

/**
 * ユーザーのフォーマット整形(フォーム)
 */
const formatInitUserDataList = (userDataList: RecordUserDataModel[]) => {
  return userDataList.map(
    (model) =>
      ({
        userId: model.userId ?? null,
        firstName: model.firstName ?? "",
        middleName: model.middleName ?? "",
        lastName: model.lastName ?? "",
        nickname: model.nickname ?? "",
        email: model.email ?? "",
        password: model.password ?? "",
        userRoles: model.userRoles ?? null,
        userProfiles: model.userProfiles
          ? model.userProfiles.map(
            (v) =>
              ({
                ...v,
                key: v.key ?? "",
                value: v.value ?? "",
              }) as UserProfiles
          )
          : null,
        userGroup: model.userGroup
          ? model.userGroup.map((v) => ({
            userGroupHistories: v.userGroupHistories
              ? {
                ...v.userGroupHistories,
                notes: v.userGroupHistories.notes ?? "",
              }
              : null,
            groups: v.groups ? v.groups : null,
          }))
          : null,
        isDeleted: model.isDeleted ?? false,
      }) as RecordUserDataModel
  );
};

/** デフォルトの表示件数 */
const defaultPageSize = 50;

/** 表示件数の選択肢 */
const pageSizeList = [50, 100, 1000];

/**
 * ユーザー登録・編集ページ
 * @returns
 */
/**
 * ユーザー管理ページコンポーネント
 * - ユーザーの一覧表示・追加・編集・削除機能を提供する
 * - CSVファイルによるユーザー一括インポートに対応する
 */
const UserManagement = () => {
  const [rows, setRows] = useState<RecordUserDataModel[]>([]);
  const [rolesOptions, setRolesOptions] = useState<Roles[]>([]);
  const [groupsOptions, setGroupsOptions] = useState<Groups[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(
    sessionStorage.getItem("searchText_UserManagement") || ""
  ); // sessionStorageに値がある場合はその値を格納。ない場合は空文字を格納。
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    // sessionStorageに値がある場合はその値を格納。ない場合はデフォルト値を格納。
    const stored = sessionStorage.getItem("visibleColomns_UserManagement");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set<string>(parsed);
        }
      } catch (e) {
        console.warn("visibleColumns の復元に失敗しました:", e);
      }
    }
    return new Set<string>(Object.keys(columnLabels));
  });
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [pageSize, setPageSize] = useState(
    Number(sessionStorage.getItem("showItems_UserManagement")) ||
    defaultPageSize
  ); // sessionStorageに値がある場合はその値を格納。ない場合はデフォルト値を格納。
  const [page, setPage] = useState(() => {
    // sessionStorageに値がある場合はその値を格納。それ以外は1をセットする。
    const stored = sessionStorage.getItem("paging_UserManagement");
    const parsed = stored ? parseInt(stored, 10) : NaN;
    return !isNaN(parsed) && parsed > 0 ? parsed : 1;
  });
  const [showSearchSettings, setShowSearchSettings] = useState(false);
  const [columnSearchTerms, setColumnSearchTerms] = useState<
    Record<string, string>
  >({});
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const fileImportRef = useRef<HTMLInputElement>(null);
  const [updateRows, setUpdateRows] = useState<RecordUserDataModel[]>([]);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [updateOrDeleteDialog, setUpdateOrDeleteDialog] = useState<{
    show: boolean;
    actionName: "更新" | "削除";
    message: string;
  }>({
    show: false,
    actionName: "更新",
    message: "",
  });
  const [showSnackbar, setShowSnackbar] = useState(false);

  const toggleColumn = (key: string) => {
    const newSet = new Set(visibleColumns);
    newSet.has(key) ? newSet.delete(key) : newSet.add(key);
    sessionStorage.setItem(
      "visibleColomns_UserManagement",
      JSON.stringify(Array.from(newSet))
    ); // 表示するカラム情報をsessionStorageに格納する。
    setVisibleColumns(newSet);
  };

  /**
   * データの検索結果をメモ化
   */
  const filteredData = useMemo(() => {
    let result = [...rows];
    // 全体検索キーワード（検索欄）
    const keywords = searchTerm.toLowerCase().split(/\s+/).filter(Boolean);
    if (keywords.length > 0) {
      result = result.filter((row) =>
        keywords.every((keyword) =>
          Object.values(getRecordUserDataModelStr(row)).some((value) =>
            value.toLowerCase().includes(keyword)
          )
        )
      );
    }
    // 項目別検索
    const activeColumnSearches = Object.entries(columnSearchTerms).filter(
      ([_, value]) => value.trim() !== ""
    );
    if (activeColumnSearches.length > 0) {
      result = result.filter((row) =>
        activeColumnSearches.every(([key, value]) => {
          switch (key) {
            case "firstName":
            case "middleName":
            case "lastName":
            case "nickname":
            case "email":
              return (row[key as keyof RecordUserDataModel] as string)
                .toLowerCase()
                .includes(value.toLowerCase());
            case "userRoles":
              return getUserRolesStr(row.userRoles)
                .toLowerCase()
                .includes(value.toLowerCase());
            case "userProfiles":
              return getUserProfilesStr(row.userProfiles)
                .toLowerCase()
                .includes(value.toLowerCase());
            case "userGroup":
              return getUserGroupStr(row.userGroup)
                .toLowerCase()
                .includes(value.toLowerCase());
            default:
              return false;
          }
        })
      );
    }
    setPage(1);
    return result;
  }, [searchTerm, columnSearchTerms, rows]);

  /**
   * 検索設定変更時のフィルタリング処理
   */
  const handleColumnSearchChange = useCallback(
    (key: string, value: string) => {
      const updated = {
        ...columnSearchTerms,
        [key]: value,
      };
      setColumnSearchTerms(updated);
      // すべての値が空文字なら sessionStorage を削除
      Object.values(updated).every((v) => v === "")
        ? sessionStorage.removeItem("columnSearchTerms_UserManagement")
        : sessionStorage.setItem(
          "columnSearchTerms_UserManagement",
          JSON.stringify(updated)
        );
    },
    [columnSearchTerms]
  );

  /**
   * フィルターの条件を初期化する
   */
  const filterReset = () => {
    // sessionStorageを削除する
    sessionStorage.removeItem("searchText_UserManagement");
    sessionStorage.removeItem("showItems_UserManagement");
    sessionStorage.removeItem("visibleColomns_UserManagement");
    sessionStorage.removeItem("columnSearchTerms_UserManagement");
    sessionStorage.removeItem("paging_UserManagement");

    // フィルター条件の初期化
    setSearchTerm("");
    setVisibleColumns(new Set(Object.keys(columnLabels)));
    setPageSize(defaultPageSize);
    setColumnSearchTerms({});
    setPage(1);
  };

  useEffect(() => {
    // 既存ユーザーを取得
    initialize();
    // 検索設定に関してsessionStorageに値がある場合はその値を反映する。
    const savedTerms = sessionStorage.getItem(
      "columnSearchTerms_UserManagement"
    );
    if (savedTerms) {
      try {
        const parsedTerms = JSON.parse(savedTerms);
        if (typeof parsedTerms === "object" && parsedTerms !== null) {
          const sanitizedTerms = Object.fromEntries(
            Object.entries(parsedTerms).filter(
              ([_, v]) => typeof v === "string" && v !== ""
            )
          ) as Record<string, string>;
          setColumnSearchTerms(sanitizedTerms);
        }
      } catch (e) {
        console.warn("Invalid sessionStorage value for columnSearchTerms");
      }
    }
  }, []);

  /**
   * 初期処理
   */
  const initialize = async () => {
    try {
      const response = await axiosRequest<RecordUserDataInitResponse>(
        "/users/initialize",
        "GET"
      );
      setRows(formatInitUserDataList(response.allRecordUserData) ?? []);
      setRolesOptions(response.allRoles ?? []);
      setGroupsOptions(response.allGroups ?? []);
      setUpdateRows([]);
      setLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * ユーザー登録／更新
   */
  const createOrUpdateUser = async (userDataList: RecordUserDataModel[]) => {
    try {
      const formattedData = formatUserDataList(userDataList);
      const response = await axiosRequest<{
        userIdList: string[];
        message: string;
      }>("/users/createOrUpdateUser", "POST", {
        data: {
          userDataList: formattedData,
        },
      });
      if (response.userIdList.length === userDataList.length) {
        setUpdateRows([]);
        setShowSnackbar(true);
        return true;
      }
    } catch (e) {
      // エラーメッセージはAxiosServiceのinterceptorで表示される
      return false;
    }
    return false;
  };

  /**
   * ユーザー一括更新／削除
   */
  const updateOrDeleteUser = async (action: boolean) => {
    setUpdateOrDeleteDialog((prev) => ({ ...prev, show: false })); // ダイアログを閉じる
    if (action) {
      const targetRows: RecordUserDataModel[] =
        updateOrDeleteDialog.actionName === "削除"
          ? rows
            .filter((row) => selectedRows.has(row.userId ?? ""))
            .map((row) => ({ ...row, isDeleted: true }))
          : updateRows;
      const result = await createOrUpdateUser(targetRows);
      if (result) {
        setUpdateRows([]);
        initialize();
      }
    }
  };

  return (
    <CustomMenu>
      {loading ? (
        <div className="flex justify-center items-center h-40">
          <CircularProgress />
        </div>
      ) : (
        <div className="p-4">
          <Box display="flex" alignItems="center" gap={2}>
            {/* 検索欄 */}
            <div className="mb-2 flex items-center gap-2">
              <TextField
                label="検索"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchTerm(value);
                  // 空の場合はsessionStorageを削除。それ以外は値を格納する。
                  value === ""
                    ? sessionStorage.removeItem("searchText_UserManagement")
                    : sessionStorage.setItem(
                      "searchText_UserManagement",
                      value
                    );
                }}
                className="w-64"
                sx={{ ml: 1, mt: 2 }}
              />
            </div>
            <Chip
              label="リセット"
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                ml: 1,
                mt: 2,
              }}
              onClick={filterReset}
            />
          </Box>
          {/* 表示設定 */}
          <Button
            sx={{ ml: 1, mt: 1 }}
            onClick={() => setShowColumnSettings(!showColumnSettings)}
            startIcon={showColumnSettings ? <ExpandLess /> : <ExpandMore />}
          >
            表示設定
          </Button>
          <Box sx={{ pl: 2, pb: 1 }}>
            {showColumnSettings && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {/* 表示件数 */}
                <Box sx={{ mb: 0 }}>
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "grey",
                      marginBottom: -6,
                    }}
                  >
                    表示件数:
                  </div>
                  <RadioGroup
                    row
                    value={pageSize.toString()}
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value));
                      sessionStorage.setItem(
                        "showItems_UserManagement",
                        e.target.value
                      ); // 表示設定が変わったら、sessionStorageに値を格納する。
                      setPage(1);
                      sessionStorage.removeItem("paging_UserManagement"); // 表示設定が変わったら、sessionStorageの値を削除する。
                    }}
                  >
                    {pageSizeList.map((num) => (
                      <FormControlLabel
                        key={num}
                        value={num.toString()}
                        control={<Radio sx={{ transform: "scale(0.6)" }} />}
                        label={`${num}件`}
                      />
                    ))}
                  </RadioGroup>
                </Box>
                {/* 表示項目 */}
                <Box>
                  <div
                    className="font-semibold mb-2"
                    style={{
                      fontWeight: "bold",
                      color: "grey",
                      marginBottom: 4,
                    }}
                  >
                    表示項目:
                  </div>
                  <Box className="flex flex-wrap gap-2">
                    {Object.entries(columnLabels).map(([key, label]) => (
                      <label key={key} className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={visibleColumns.has(key)}
                          onChange={() => toggleColumn(key)}
                        />
                        {label}
                      </label>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
          {/* 検索設定 */}
          <Button
            sx={{ ml: 1, mt: -1 }}
            onClick={() => setShowSearchSettings(!showSearchSettings)}
            startIcon={showSearchSettings ? <ExpandLess /> : <ExpandMore />}
          >
            検索設定
          </Button>
          <Box sx={{ pl: 2, pb: 2 }}>
            {showSearchSettings && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {Object.entries(columnLabels)
                  .filter(([key]) => key !== "actions")
                  .map(([key, label]) => (
                    <Box
                      key={key}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 100,
                      }}
                    >
                      <div style={{ fontSize: "1rem", marginBottom: 4 }}>
                        {label}
                      </div>
                      <TextField
                        size="small"
                        variant="outlined"
                        value={columnSearchTerms[key] || ""}
                        onChange={(e) =>
                          handleColumnSearchChange(key, e.target.value)
                        }
                        sx={{
                          width: 140,
                          "& input": {
                            padding: "4px 8px",
                          },
                        }}
                        slotProps={{
                          input: {
                            startAdornment: columnSearchTerms[
                              key
                            ] ? undefined : (
                              <InputAdornment position="start">
                                <Search fontSize="small" />
                              </InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Box>
                  ))}
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mx: 1,
              my: 0,
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={() => setShowCreateUserDialog(true)}
              >
                ユーザー新規作成
              </Button>
              {updateRows.length > 0 && (
                <Button
                  variant="contained"
                  startIcon={<ChangeCircle />}
                  onClick={() =>
                    setUpdateOrDeleteDialog({
                      show: true,
                      actionName: "更新",
                      message: `編集した ${updateRows.length} 件のユーザーを更新しますか？`,
                    })
                  }
                >
                  更新
                </Button>
              )}
              {selectedRows.size > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() =>
                    setUpdateOrDeleteDialog({
                      show: true,
                      actionName: "削除",
                      message: `選択された ${selectedRows.size} 件のユーザーを削除しますか？`,
                    })
                  }
                >
                  削除
                </Button>
              )}
            </Box>
            {/*TODO: CSVインポートの仕様見直しのため一時的にコメントアウト 
             <Button
              variant="outlined"
              onClick={() => fileImportRef.current?.click()}
            >
              CSVインポート
            </Button> */}
            <input
              hidden
              aria-label="fileImport"
              type="file"
              onChange={(e) =>
                handleFileImport(
                  e,
                  rolesOptions,
                  groupsOptions,
                  setRows,
                  setUpdateRows
                )
              }
              accept=".csv"
              ref={fileImportRef}
            />
          </Box>
          {/* テーブル */}
          <UserManageTable
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            filteredData={filteredData}
            rows={rows}
            setRows={setRows}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            visibleColumns={visibleColumns}
            rolesOptions={rolesOptions}
            groupsOptions={groupsOptions}
            setUpdateRows={setUpdateRows}
          />
        </div>
      )}
      {/* ユーザー新規作成ダイアログ */}
      <CreateUserDialog
        open={showCreateUserDialog}
        handleClose={(completed: boolean) => {
          setShowCreateUserDialog(false);
          if (completed) {
            initialize();
          }
        }}
        rolesOptions={rolesOptions}
        groupsOptions={groupsOptions}
        createOrUpdateUser={createOrUpdateUser}
      />
      {/* 更新／削除確認ダイアログ */}
      <Dialog
        open={updateOrDeleteDialog.show}
        onClose={() => updateOrDeleteUser(false)}
      >
        <DialogTitle>{updateOrDeleteDialog.actionName}確認</DialogTitle>
        <DialogContent>
          <DialogContentText>{updateOrDeleteDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => updateOrDeleteUser(false)}>キャンセル</Button>
          <Button
            onClick={() => {
              updateOrDeleteUser(true);
              // 選択をリセットする
              setSelectedRows(new Set());
            }}
            color={
              updateOrDeleteDialog.actionName === "削除" ? "error" : "primary"
            }
          >
            {updateOrDeleteDialog.actionName}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        message="ユーザーの登録／更新が完了しました。"
        autoHideDuration={6000}
        action={
          <IconButton
            size="small"
            aria-label="closeSnackbar"
            color="inherit"
            onClick={() => setShowSnackbar(false)}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      />
    </CustomMenu>
  );
};

export default UserManagement;
